<template>
  <div class="relative min-h-screen flex items-center justify-center overflow-hidden p-6">
    <!-- Background -->
    <div class="absolute inset-0 bg-primary-dark">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="absolute inset-0 dot-grid opacity-[0.03]"></div>
    </div>

    <div class="relative z-10 w-full max-w-[440px]">
      <!-- White card -->
      <div class="bg-white rounded-2xl p-8 sm:p-10 shadow-2xl">
        <div class="flex justify-center mb-8">
          <img src="@/assets/images/sellhub-logo.png" alt="SellHub" class="h-10 w-auto" />
        </div>

        <div class="text-center mb-8">
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Mot de passe oublié ?</h1>
          <p class="text-gray-500 text-sm">Entrez votre email pour recevoir un lien de réinitialisation</p>
        </div>

        <!-- Success -->
        <div v-if="success" class="flex flex-col items-center text-center bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <div class="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
            <i class="fas fa-check text-green-600 text-lg"></i>
          </div>
          <p class="text-green-700 text-sm leading-relaxed">
            Un lien de réinitialisation a été envoyé à votre adresse email. Veuillez vérifier votre boîte de réception.
          </p>
        </div>

        <!-- Error -->
        <div v-if="error && !success" class="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
          <i class="fas fa-exclamation-circle mt-0.5 text-red-500"></i>
          <span>{{ error }}</span>
        </div>

        <form v-if="!success" @submit.prevent="handleSubmit" class="space-y-5">
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email</label>
            <div class="relative">
              <i class="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                v-model="email"
                type="email"
                placeholder="votre@email.com"
                required
                :class="[
                  'w-full pl-11 pr-4 py-3 bg-gray-50 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all',
                  validationErrors.email ? 'border-red-400' : 'border-gray-200'
                ]"
              />
            </div>
            <p v-if="validationErrors.email" class="text-red-500 text-xs mt-1.5">{{ validationErrors.email[0] }}</p>
          </div>

          <button
            type="submit"
            :disabled="isLoading"
            class="w-full py-3.5 bg-primary hover:bg-primary-dark text-white font-semibold text-sm rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/25 hover:shadow-primary/40 flex items-center justify-center gap-2"
          >
            <i v-if="isLoading" class="fas fa-spinner fa-spin text-sm"></i>
            {{ isLoading ? 'Envoi en cours...' : 'Envoyer le lien' }}
          </button>
        </form>

        <div class="text-center mt-6 pt-6 border-t border-gray-100">
          <router-link :to="{ name: 'login' }" class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors">
            <i class="fas fa-arrow-left text-xs"></i>
            Retour à la connexion
          </router-link>
        </div>
      </div>

      <div class="text-center mt-6 space-y-1">
        <p class="text-gray-400 text-xs">© {{ new Date().getFullYear() }} SellHub. Tous droits réservés.</p>
        <p class="text-gray-400 text-xs">
          Powered by
          <a href="https://digita.sn" target="_blank" class="text-primary font-semibold hover:text-primary-dark transition-colors">Digita SN</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { authService } from '@/services'
import { defineComponent, ref } from 'vue'

export default defineComponent({
    name: 'ForgotPasswordView',

    setup() {
        const email = ref('')
        const isLoading = ref(false)
        const error = ref(null)
        const success = ref(false)
        const validationErrors = ref({})

        const handleSubmit = async () => {
            isLoading.value = true
            error.value = null
            validationErrors.value = {}
            success.value = false

            try {
                await authService.requestResetPasswordLink(email.value)
                success.value = true
            } catch (err) {
                console.error('Erreur lors de la demande de réinitialisation :', err)

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {}
                    error.value = err.response.data.message || 'Erreur de validation'
                } else {
                    error.value = err.response?.data?.message || 'Une erreur est survenue lors de la demande de réinitialisation'
                }
            } finally {
                isLoading.value = false
            }
        }

        return {
            email,
            isLoading,
            error,
            success,
            validationErrors,
            handleSubmit
        }
    }
})
</script>

<style scoped>
.dot-grid {
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.8) 1px, transparent 1px);
  background-size: 30px 30px;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(13, 148, 136, 0.5), transparent 70%);
  top: -10%;
  right: 20%;
  animation: float-1 20s ease-in-out infinite;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.4), transparent 70%);
  bottom: -5%;
  left: 10%;
  animation: float-2 25s ease-in-out infinite;
}

@keyframes float-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(40px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

@keyframes float-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-30px, 40px) scale(1.05); }
  66% { transform: translate(30px, -20px) scale(0.9); }
}
</style>
